import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { deleteLead, downloadLeadsCsv, getLeads } from '../utils/leads';
import AppHeader from './AppHeader';
import { UpgradeNote } from './Upgrade';
import { hasPaidAccess } from '../utils/billing';
import { errorBox, inputClass, secondaryButton } from './AuthLayout';

const when = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** /leads — people who shared their details from your card (or your company's cards). */
export default function Leads() {
  const { user, memberships, canManageOrg } = useAuth();
  const companies = memberships
    .filter((m) => m.organizations?.kind === 'company' && canManageOrg(m.org_id))
    .map((m) => m.organizations);
  const [scope, setScope] = useState('mine');
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    let active = true;
    setLeads(null);
    setError('');
    getLeads(scope === 'mine' ? { userId: user.id } : { orgId: scope })
      .then((rows) => active && setLeads(rows))
      .catch((e) => active && setError(e.message || 'Could not load leads.'));
    return () => { active = false; };
  }, [scope, user.id]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !leads) return leads;
    return leads.filter((l) => [l.full_name, l.email, l.phone, l.company, l.note, l.cards?.full_name]
      .some((v) => (v || '').toLowerCase().includes(q)));
  }, [leads, search]);

  // The lead form needs Pro / Teams. Old leads stay here either way.
  const scopeOrgs = scope === 'mine' ? memberships.map((m) => m.organizations).filter(Boolean) : companies.filter((c) => c.id === scope);
  const unpaid = scopeOrgs.length > 0 && !scopeOrgs.some(hasPaidAccess)
    ? scopeOrgs.find((o) => o.kind === 'personal') || scopeOrgs[0]
    : null;
  const team = scope !== 'mine';
  const scopeName = team ? companies.find((c) => c.id === scope)?.name : 'My leads';

  const remove = async (id) => {
    try {
      await deleteLead(id);
      setLeads((rows) => rows.filter((r) => r.id !== id));
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirming(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <h1 className="text-[34px] leading-10 font-extrabold tracking-tight">Leads</h1>
            <p className="text-[15px] text-ink-muted mt-1">
              People who shared their details when they saved {team ? `a ${scopeName} card` : 'your card'}.
            </p>
          </div>
          <button onClick={() => downloadLeadsCsv(shown || [], `carduzo-leads-${new Date().toISOString().slice(0, 10)}.csv`)}
            disabled={!shown?.length} className={secondaryButton}>
            Export CSV
          </button>
        </div>

        {unpaid && (
          <UpgradeNote org={unpaid} canManage={canManageOrg(unpaid.id)}>
            {leads?.length
              ? `Your lead form is off, so no new leads are coming in. The leads below stay yours to view and export. Upgrade to ${unpaid.kind === 'company' ? 'Teams' : 'Pro'} to collect new ones.`
              : `Collect leads with ${unpaid.kind === 'company' ? 'Teams' : 'Pro'}: visitors are invited to share their name and email when they save your contact, and you get an email for each one.`}
          </UpgradeNote>
        )}

        <div className="flex flex-wrap gap-3">
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads"
            aria-label="Search leads" className={`${inputClass.replace('w-full ', '')} flex-1 min-w-[220px] max-w-md`} />
          {companies.length > 0 && (
            <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Whose leads"
              className={`${inputClass.replace('w-full ', '')} w-auto`}>
              <option value="mine">My leads</option>
              {companies.map((c) => <option key={c.id} value={c.id}>Everyone at {c.name}</option>)}
            </select>
          )}
        </div>

        {error && <div className={errorBox}>{error}</div>}

        {shown === null && !error && <p className="text-ink-muted">Loading leads...</p>}

        {shown?.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-line-strong p-10 text-center max-w-xl mx-auto">
            <div className="w-12 h-12 mx-auto rounded-full bg-blue-soft flex items-center justify-center mb-4" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#265EE6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
            </div>
            {search ? <p className="text-ink-muted">No leads match "{search}".</p> : (
              <>
                <h2 className="text-xl font-bold">No leads yet</h2>
                <p className="text-ink-muted mt-2">
                  When someone taps <strong className="text-ink">Save contact</strong> on your card, they're invited to share their details.
                  Those people show up here, and you get an email.
                </p>
                <Link to="/" className="inline-block mt-4 text-sm font-semibold text-blue hover:underline">Share your card</Link>
              </>
            )}
          </div>
        )}

        {shown?.length > 0 && (
          <>
            <p className="text-sm text-ink-muted">{shown.length} {shown.length === 1 ? 'lead' : 'leads'}</p>
            <ul className="grid gap-3 lg:hidden">
              {shown.map((l) => (
                <li key={l.id} className="bg-white rounded-2xl border border-line p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{l.full_name}</p>
                      {l.company && <p className="text-sm text-ink-muted">{l.company}</p>}
                    </div>
                    <span className="text-xs text-ink-muted whitespace-nowrap">{when(l.created_at)}</span>
                  </div>
                  <Contact lead={l} />
                  {l.note && <p className="text-sm text-ink bg-surface rounded-lg px-3 py-2 whitespace-pre-line">{l.note}</p>}
                  <div className="flex items-center justify-between gap-3 pt-1">
                    {team ? <span className="text-xs text-ink-muted">Card: {l.cards?.full_name}</span> : <span />}
                    <DeleteButton id={l.id} confirming={confirming} setConfirming={setConfirming} onDelete={remove} />
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden lg:block bg-white rounded-2xl border border-line overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left">
                  <tr className="text-xs font-semibold tracking-wider uppercase text-ink-muted">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Note</th>
                    {team && <th className="px-4 py-3">Card</th>}
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {shown.map((l) => (
                    <tr key={l.id} className="align-top">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink">{l.full_name}</div>
                        {l.company && <div className="text-ink-muted">{l.company}</div>}
                      </td>
                      <td className="px-4 py-3"><Contact lead={l} /></td>
                      <td className="px-4 py-3 max-w-[320px] text-ink whitespace-pre-line">{l.note || <span className="text-ink-muted">—</span>}</td>
                      {team && <td className="px-4 py-3 whitespace-nowrap">{l.cards?.full_name}</td>}
                      <td className="px-4 py-3 whitespace-nowrap text-ink-muted">{when(l.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <DeleteButton id={l.id} confirming={confirming} setConfirming={setConfirming} onDelete={remove} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Contact({ lead }) {
  return (
    <div className="flex flex-col gap-0.5">
      {lead.email && <a href={`mailto:${lead.email}`} className="text-blue hover:underline break-all">{lead.email}</a>}
      {lead.phone && <a href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`} className="text-blue hover:underline">{lead.phone}</a>}
    </div>
  );
}

function DeleteButton({ id, confirming, setConfirming, onDelete }) {
  if (confirming === id) {
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        <button onClick={() => onDelete(id)} className="h-8 px-3 rounded-lg bg-coral-ink text-white text-xs font-semibold">Delete</button>
        <button onClick={() => setConfirming(null)} className="h-8 px-2 text-xs font-medium text-ink-muted hover:text-ink">Keep</button>
      </span>
    );
  }
  return (
    <button onClick={() => setConfirming(id)} className="h-8 px-2 text-xs font-medium text-ink-muted hover:text-coral-ink">Delete</button>
  );
}
